import { Component, useEffect, useMemo, useRef, useState } from "react";
import { Mic, MicOff, Video, VideoOff, Loader2, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";

let sdk;
const loadSdk = () => (sdk ||= import("@100mslive/hms-video-store"));

function useSel(store, selector) {
  const [v, setV] = useState(() => store.getState(selector));
  useEffect(() => { setV(store.getState(selector)); return store.subscribe(setV, selector); }, [store, selector]);
  return v;
}

function Tile({ peer, hms, small }) {
  const ref = useRef(null);
  const { actions, store, m } = hms;
  const vSel = useMemo(() => m.selectVideoTrackByID(peer.videoTrack), [m, peer.videoTrack]);
  const aSel = useMemo(() => m.selectIsPeerAudioEnabled(peer.id), [m, peer.id]);
  const track = useSel(store, vSel);
  const audioOn = useSel(store, aSel);
  const videoOn = !!(track && track.enabled);
  useEffect(() => {
    const el = ref.current;
    if (!peer.videoTrack || !el || !videoOn) return undefined;
    actions.attachVideo(peer.videoTrack, el).catch(() => {});
    return () => { actions.detachVideo(peer.videoTrack, el).catch(() => {}); };
  }, [actions, peer.videoTrack, videoOn]);
  return (
    <div className={`relative overflow-hidden bg-[#13232F] h-full w-full ${small ? "rounded-xl border border-white/20 shadow-lg" : ""}`} data-testid={peer.isLocal ? "hms-tile-local" : "hms-tile-remote"}>
      <video ref={ref} autoPlay muted playsInline className={`h-full w-full object-cover ${peer.isLocal ? "-scale-x-100" : ""} ${videoOn ? "" : "hidden"}`} />
      {!videoOn && <div className="absolute inset-0 flex items-center justify-center"><span className={`${small ? "h-10 w-10" : "h-20 w-20"} rounded-full bg-white/10 flex items-center justify-center`}><UserRound className={small ? "h-5 w-5 text-white/70" : "h-9 w-9 text-white/70"} /></span></div>}
      <span className="absolute left-2 bottom-2 flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[11px] text-white">
        {audioOn ? <Mic className="h-3 w-3" /> : <MicOff className="h-3 w-3 text-red-300" />}{peer.isLocal ? "You" : peer.name}
      </span>
    </div>
  );
}

function Room({ data, onJoin, onLeave, onError, callRef }) {
  const [hms, setHms] = useState(null);
  const cb = useRef({ onJoin, onLeave, onError });
  cb.current = { onJoin, onLeave, onError };
  useEffect(() => {
    let alive = true, inst = null, offs = [];
    loadSdk().then((m) => {
      if (!alive) return;
      const r = new m.HMSReactiveStore();
      r.triggerOnSubscribe();
      inst = { actions: r.getActions(), store: r.getStore(), m };
      let joined = false;
      offs.push(inst.store.subscribe((c) => { if (c && !joined) { joined = true; cb.current.onJoin?.(); } }, m.selectIsConnectedToRoom));
      offs.push(r.getNotifications().onNotification((n) => {
        if (n.type === m.HMSNotificationTypes.ROOM_ENDED || n.type === m.HMSNotificationTypes.REMOVED_FROM_ROOM) cb.current.onLeave?.("ended");
        else if (n.type === m.HMSNotificationTypes.ERROR && n.data?.isTerminal) cb.current.onError?.(n.data);
      }));
      callRef && (callRef.current = { leave: () => inst.actions.leave() });
      setHms(inst);
      inst.actions.join({ userName: data.display_name, authToken: data.auth_token, rememberDeviceSelection: true,
        settings: { isAudioMuted: false, isVideoMuted: data.mode === "audio" } }).catch((e) => cb.current.onError?.(e));
    }).catch((e) => cb.current.onError?.(e));
    return () => { alive = false; offs.forEach((f) => f && f()); inst?.actions.leave().catch(() => {}); };
  }, [data.auth_token, data.display_name, data.mode, callRef]);
  if (!hms) return <Connecting />;
  return <Stage hms={hms} audioOnly={data.mode === "audio"} />;
}

const Connecting = () => <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/80 text-sm"><Loader2 className="h-6 w-6 animate-spin" />Connecting securely…</div>;

function Stage({ hms, audioOnly }) {
  const { actions, store, m } = hms;
  const connected = useSel(store, m.selectIsConnectedToRoom);
  const peers = useSel(store, m.selectPeers);
  const mic = useSel(store, m.selectIsLocalAudioEnabled);
  const cam = useSel(store, m.selectIsLocalVideoEnabled);
  if (!connected) return <Connecting />;
  const local = peers.find((p) => p.isLocal);
  const remotes = peers.filter((p) => !p.isLocal);
  return (
    <div className="absolute inset-0" data-testid="hms-stage">
      {remotes.length ? (
        <div className={`absolute inset-0 grid gap-1 ${remotes.length > 1 ? "grid-cols-1 sm:grid-cols-2 grid-rows-2 sm:grid-rows-1" : ""}`} data-testid="hms-remote-grid">
          {remotes.map((r) => <div key={r.id} className="relative min-h-0"><Tile peer={r} hms={hms} /></div>)}
        </div>) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/80 text-sm text-center px-6" data-testid="hms-waiting">
          <Loader2 className="h-5 w-5 animate-spin" />Waiting for the other participant to join…
        </div>)}
      {local && <div className="absolute right-3 top-3 h-28 w-40 sm:h-36 sm:w-52"><Tile key={local.id} peer={local} hms={hms} small /></div>}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
        <Button size="icon" variant="secondary" className="rounded-full" onClick={() => actions.setLocalAudioEnabled(!mic)} data-testid="video-toggle-audio" aria-label="Toggle microphone">{mic ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4 text-red-600" />}</Button>
        {!audioOnly && <Button size="icon" variant="secondary" className="rounded-full" onClick={() => actions.setLocalVideoEnabled(!cam)} data-testid="video-toggle-video" aria-label="Toggle camera">{cam ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4 text-red-600" />}</Button>}
      </div>
    </div>
  );
}

class Boundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(e) { this.props.onError?.(e); }
  render() { return this.state.failed ? null : this.props.children; }
}

export function HmsCall(props) {
  return <Boundary onError={props.onError}><Room {...props} /></Boundary>;
}
